/**
 * Bridge ColumnMapper state ↔ Field Mapping named sets on connector type `csv`.
 * Profiles live in connector_configs.field_mappings (no parallel tables).
 */

import type { ColumnMappingState } from '../../components/prioritization/import-export/ColumnMapper';
import {
  loadFieldMappingConfig,
  saveFieldMappingConfig,
  type FieldMappingConfig,
  type FieldMappingEntry,
  type FieldMappingSet,
} from '../fieldMappingRegistry';
import { resolveActiveMappingSet } from '../fieldMappingApply';

export const CSV_CONNECTOR_TYPE = 'csv' as const;

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `csv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Convert mapper state (excel header → app field) into FieldMappingSet entries. */
export function columnMappingsToSetEntries(
  mappings: ColumnMappingState,
): FieldMappingEntry[] {
  const nowEntries: FieldMappingEntry[] = [];
  for (const [externalFieldKey, internalFieldKey] of Object.entries(mappings)) {
    if (!internalFieldKey) continue;
    nowEntries.push({
      id: newId(),
      externalFieldKey,
      externalFieldLabel: externalFieldKey,
      internalFieldKey,
      direction: 'import',
      enabled: true,
      valueMap: [],
    });
  }
  return nowEntries;
}

/** Convert a saved FieldMappingSet back into ColumnMapper state. */
export function setEntriesToColumnMappings(set: FieldMappingSet): ColumnMappingState {
  const out: ColumnMappingState = {};
  for (const entry of set.entries) {
    if (!entry.enabled || entry.direction === 'export') continue;
    out[entry.externalFieldKey] = entry.internalFieldKey;
  }
  return out;
}

export async function listCsvImportProfiles(
  workspaceId: string,
): Promise<FieldMappingSet[]> {
  const config = await loadFieldMappingConfig(workspaceId, CSV_CONNECTOR_TYPE);
  return config.sets.filter((s) =>
    s.entries.some((e) => e.direction === 'import' || e.direction === 'both'),
  );
}

/**
 * Save (or replace) a named CSV import mapping profile on the csv connector config.
 * Sets importSetId to the saved set so Field Mappings shows it as active for import.
 */
export async function saveCsvImportProfile(params: {
  workspaceId: string;
  name: string;
  description?: string;
  mappings: ColumnMappingState;
  setId?: string;
}): Promise<{ set: FieldMappingSet; error: string | null }> {
  const config = await loadFieldMappingConfig(params.workspaceId, CSV_CONNECTOR_TYPE);
  const now = new Date().toISOString();
  const entries = columnMappingsToSetEntries(params.mappings);
  const existingIdx = params.setId
    ? config.sets.findIndex((s) => s.id === params.setId)
    : -1;

  const set: FieldMappingSet = {
    id: existingIdx >= 0 ? config.sets[existingIdx].id : newId(),
    connectorType: CSV_CONNECTOR_TYPE,
    name: params.name.trim() || 'CSV Import Profile',
    description: params.description?.trim()
      ?? 'Saved from Items list Import → Map Columns',
    entries,
    createdAt: existingIdx >= 0 ? config.sets[existingIdx].createdAt : now,
    updatedAt: now,
  };

  const sets =
    existingIdx >= 0
      ? config.sets.map((s, i) => (i === existingIdx ? set : s))
      : [...config.sets, set];

  const next: FieldMappingConfig = {
    ...config,
    version: config.version === 3 ? 3 : 2,
    sets,
    importSetId: set.id,
  };

  const { error } = await saveFieldMappingConfig(
    params.workspaceId,
    CSV_CONNECTOR_TYPE,
    next,
  );
  return { set, error };
}

export async function loadCsvImportProfileMappings(
  workspaceId: string,
  setId?: string | null,
): Promise<{ set: FieldMappingSet | null; mappings: ColumnMappingState }> {
  const config = await loadFieldMappingConfig(workspaceId, CSV_CONNECTOR_TYPE);
  const set =
    (setId ? config.sets.find((s) => s.id === setId) : null)
    ?? resolveActiveMappingSet(config, 'import').set
    ?? config.sets[0]
    ?? null;
  return {
    set,
    mappings: set ? setEntriesToColumnMappings(set) : {},
  };
}
